<script setup lang="ts">
import { computed, useAttrs } from 'vue'

defineOptions({
  inheritAttrs: false,
})

const attrs = useAttrs()

const props = withDefaults(
  defineProps<{
    modelValue?: boolean
  }>(),
  {
    modelValue: undefined,
  }
)

const emit = defineEmits(['update:modelValue'])

const inputAttrs = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { class: _class, ...rest } = attrs
  return rest
})

const isChecked = computed({
  get() {
    if (props.modelValue !== undefined) {
      return props.modelValue
    }
    return 'checked' in attrs
  },
  set(val) {
    emit('update:modelValue', val)
  },
})
</script>

<template>
  <input
    v-bind="inputAttrs"
    v-model="isChecked"
    class="glp-switch"
    :class="attrs.class"
    type="checkbox"
  />
</template>

<style>
.glp-switch,
/* These selectors are to here to prevent tailwind from overriding styles */
.glp-switch[type='checkbox'],
.glp-switch[type='checkbox']:checked {
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--glp-switch-track-width, 56px);
  height: var(--glp-switch-track-height, 32px);
  padding: 0 var(--glp-switch-track-padding-horizontal, 4px);
  border-radius: var(--glp-switch-track-border-radius, 1000px);
  border: var(--glp-switch-track-border-width, 1px) solid
    var(
      --glp-switch-track-color-border,
      var(--glp-switch-track-default-color-border, #b6c6cf)
    );
  background: var(--glp-switch-track-background, #e2eaed);
  color: transparent;
  transition: background-color 0.1s 0.1s;
}

.glp-switch::before {
  translate: calc(
      (var(--glp-switch-track-height) - var(--glp-switch-track-width)) / 2
    )
    0;
  content: '';
  display: block;
  width: var(--glp-switch-handle-size, 24px);
  height: auto;
  flex-shrink: 0;
  aspect-ratio: 1;
  border-radius: 500px;
  background: var(
    --glp-switch-handle-background,
    var(--glp-switch-handle-default-background, #082330)
  );
  transition:
    translate 0.2s,
    background-color 0.2s;
}

/* States */
.glp-switch:hover {
  --glp-switch-handle-background: var(
    --glp-switch-handle-hover-background,
    #4c6575
  );
}

.glp-switch:active {
  --glp-switch-handle-background: var(
    --glp-switch-handle-pressed-background,
    #01090c
  );
}

.glp-switch:checked {
  --glp-switch-handle-background: var(
    --glp-switch-handle-selected-background,
    #ffffff
  );
  --glp-switch-track-background: var(
    --glp-switch-track-selected-background,
    #ff7300
  );
  --glp-switch-track-color-border: var(--glp-switch-track-background);
}

.glp-switch:checked:hover {
  --glp-switch-handle-background: var(
    --glp-switch-handle-selected-pressed-background,
    #e2eaed
  );
}

.glp-switch:checked::before {
  translate: calc(
      (var(--glp-switch-track-height) - var(--glp-switch-track-width)) / -2
    )
    0;
}

.glp-switch:focus-visible {
  outline: 3px solid var(--glp-general-color-focus);
  outline-offset: 3px;
  isolation: isolate; /** Ensure focus ring stacks above other elements */
}

.glp-switch:disabled {
  opacity: 0.5;
  pointer-events: none;
}
</style>
